/**
 * Parent weekly medication adherence grid — the digital version of the paper
 * weekly tracking sheet.
 *
 * Rows = each medication (one per signed order) plus a special "Weight" row.
 * Columns = the 7 days of the selected week. Each med×day cell lists that day's
 * scheduled slots (time · status · dose), color/icon-coded by administration
 * status. A week pager moves between weeks, bounded below by the treatment
 * start date and above by the current week (no paging into the future).
 *
 * The grid is read-only: it shows the RECORD of doses given (and the dose in
 * effect each day, which changes as the doctor adjusts for weight). The server
 * endpoint GET /api/medications/grid already computes statuses and effective
 * doses; this component just renders them.
 *
 * App-specific labels that are not in the shared i18n catalog use a tiny inline
 * FR/EN map keyed off the current locale (the accepted pattern, see
 * ReportUpload.tsx / SerologyImport.tsx). Med names and dose statuses reuse the
 * shared t() catalog (concept.* / admin.*).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, toCalendarDate, weekdayOf, compareDates, type CalendarDate } from '@toxofollow/shared';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card } from '../../components/ui.js';
import { QueryState } from '../../components/form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  title: { fr: 'Suivi hebdomadaire des médicaments', en: 'Weekly medication tracking' },
  subtitle: {
    fr: 'Le relevé des doses réellement données, jour par jour.',
    en: 'The record of doses actually given, day by day.',
  },
  weight: { fr: 'Poids', en: 'Weight' }, // also t('growth.weight'); kept as fallback
  medication: { fr: 'Médicament', en: 'Medication' },
  prev: { fr: 'Semaine précédente', en: 'Previous week' },
  next: { fr: 'Semaine suivante', en: 'Next week' },
  empty: { fr: 'Aucun médicament à afficher pour cette semaine.', en: 'No medication to show for this week.' },
  late: { fr: 'en retard', en: 'late' },
  legend: { fr: 'Légende', en: 'Legend' },
  unverified: { fr: 'poids non vérifié', en: 'unverified weight' },
  given: { fr: 'doses données cette semaine', en: 'doses given this week' },
  toLog: { fr: 'à enregistrer', en: 'to log' },
} as const;

type Lang = 'fr' | 'en';

// Administration statuses returned per cell, plus the two synthetic slot states.
type CellStatus =
  | 'taken'
  | 'late'
  | 'missed'
  | 'partial'
  | 'refused'
  | 'vomited_after_dose'
  | 'held_by_doctor'
  | 'not_applicable'
  | 'unknown'
  | 'upcoming'
  | 'not_logged';

interface GridMed {
  orderId: string;
  concept: string;
  brandName: string | null;
}
interface GridCell {
  orderId: string;
  date: string;
  time: string;
  status: CellStatus;
  dose: string | null;
  unit: string | null;
  actualTime: string | null;
}
interface GridWeight {
  date: string;
  weightGrams: string | null;
  verified: boolean;
}
interface GridSummary {
  scheduled: number;
  given: number;
  missed: number;
  notLogged: number;
  pct: number | null;
}
interface GridResponse {
  from: string;
  to: string;
  days: string[];
  meds: GridMed[];
  cells: GridCell[];
  weights: GridWeight[];
  summary: GridSummary;
}
interface PatientResponse {
  patient: { treatmentStartDate: string | null };
}

// Visual treatment per status: text + dot colors, and whether a dose is shown.
type Tone = 'green' | 'red' | 'amber' | 'muted' | 'upcoming';
const STATUS_TONE: Record<CellStatus, Tone> = {
  taken: 'green',
  late: 'green',
  missed: 'red',
  refused: 'red',
  partial: 'amber',
  vomited_after_dose: 'amber',
  held_by_doctor: 'muted',
  not_applicable: 'muted',
  unknown: 'muted',
  not_logged: 'muted',
  upcoming: 'upcoming',
};
const TONE_CLASS: Record<Tone, string> = {
  green: 'text-green-700',
  red: 'text-red-700',
  amber: 'text-amber-700',
  muted: 'text-slate-400',
  upcoming: 'text-slate-300',
};
const STATUS_ICON: Record<CellStatus, string> = {
  taken: '✓',
  late: '✓',
  missed: '✗',
  refused: '✗',
  partial: '◐',
  vomited_after_dose: '⚠',
  held_by_doctor: '⏸',
  not_applicable: '—',
  unknown: '?',
  not_logged: '—',
  upcoming: '·',
};

// Weekday order for our Monday-first week, mapped from weekdayOf() codes.
const WEEKDAY_INDEX: Record<ReturnType<typeof weekdayOf>, number> = {
  mon: 0,
  tue: 1,
  wed: 2,
  thu: 3,
  fri: 4,
  sat: 5,
  sun: 6,
};

/** Monday (00:00) of the ISO week containing `date`, as a calendar date. */
function startOfWeek(date: CalendarDate): CalendarDate {
  return addDays(date, -WEEKDAY_INDEX[weekdayOf(date)]);
}

const today = (): CalendarDate => toCalendarDate(new Date());

/** Short localized day header, e.g. "lun. 02/06" / "Mon 02/06". */
function dayHeader(date: CalendarDate, locale: string): { name: string; dm: string } {
  const d = new Date(`${date}T00:00:00Z`);
  const name = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(d);
  const dm = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(d);
  return { name, dm };
}

/** Week range label, e.g. "2–8 juin 2026" / "2–8 June 2026". */
function weekRangeLabel(from: CalendarDate, to: CalendarDate, locale: string): string {
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  const dayFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' });
  const monthYearFmt = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  if (sameMonth) {
    return `${dayFmt.format(a)}–${dayFmt.format(b)} ${monthYearFmt.format(b)}`;
  }
  const full = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  return `${full.format(a)} – ${full.format(b)}`;
}

function kg(grams: string | null): string | null {
  if (grams == null || grams === '') return null;
  const n = Number(grams);
  if (!Number.isFinite(n)) return null;
  return `${(n / 1000).toFixed(2)} kg`;
}

export function ParentMedGrid() {
  const { t, locale } = useI18n();
  const lang: Lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  // The current (this-week) Monday is the upper bound for paging.
  const currentWeekStart = useMemo(() => startOfWeek(today()), []);
  const [weekStart, setWeekStart] = useState<CalendarDate>(currentWeekStart);
  const weekEnd = addDays(weekStart, 6);

  // Treatment start = lower bound for the pager (fall back to 12 weeks ago).
  const patientQ = useQuery({
    queryKey: ['patient'],
    queryFn: () => api.get<PatientResponse>('/patient'),
  });
  const lowerBound = useMemo<CalendarDate>(() => {
    const start = patientQ.data?.patient.treatmentStartDate;
    if (start) return startOfWeek(start);
    return addDays(currentWeekStart, -7 * 12);
  }, [patientQ.data, currentWeekStart]);

  const grid = useQuery({
    queryKey: ['med-grid', weekStart, weekEnd],
    queryFn: () => api.get<GridResponse>(`/medications/grid?from=${weekStart}&to=${weekEnd}`),
  });

  // The 7 days of the week — prefer the server's inclusive list, else derive.
  const days: CalendarDate[] = useMemo(() => {
    if (grid.data?.days?.length) return grid.data.days;
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [grid.data, weekStart]);

  // Index cells by orderId+date for O(1) lookup, sorted by scheduled time.
  const cellsByMedDay = useMemo(() => {
    const map = new Map<string, GridCell[]>();
    for (const c of grid.data?.cells ?? []) {
      const key = `${c.orderId} ${c.date}`;
      const list = map.get(key);
      if (list) list.push(c);
      else map.set(key, [c]);
    }
    for (const list of map.values()) list.sort((x, y) => x.time.localeCompare(y.time));
    return map;
  }, [grid.data]);

  const weightByDay = useMemo(() => {
    const map = new Map<string, GridWeight>();
    for (const w of grid.data?.weights ?? []) map.set(w.date, w);
    return map;
  }, [grid.data]);

  const meds = grid.data?.meds ?? [];
  const isEmpty = !grid.isLoading && !grid.isError && meds.length === 0;

  const canPrev = compareDates(weekStart, lowerBound) > 0;
  const canNext = compareDates(weekStart, currentWeekStart) < 0;
  const goPrev = () => canPrev && setWeekStart((w) => addDays(w, -7));
  const goNext = () => canNext && setWeekStart((w) => addDays(w, 7));

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-800 mb-1">{tr(L.title)}</h1>
      <p className="text-sm text-slate-500 mb-4">{tr(L.subtitle)}</p>

      {/* Week pager */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev}
          aria-label={tr(L.prev)}
          className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-800 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          ◀
        </button>
        <div className="flex-1 text-center text-sm font-semibold text-slate-700">
          {weekRangeLabel(weekStart, weekEnd, locale)}
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={!canNext}
          aria-label={tr(L.next)}
          className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-800 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          ▶
        </button>
      </div>

      {/* Logging summary for the displayed week (a tracking metric, not a clinical judgement). */}
      {grid.data?.summary && grid.data.summary.scheduled > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-sm text-slate-700">
          <strong>
            {grid.data.summary.given}/{grid.data.summary.scheduled}
          </strong>{' '}
          {tr(L.given)}
          {grid.data.summary.pct != null ? ` · ${grid.data.summary.pct}%` : ''}
          {grid.data.summary.notLogged > 0 ? (
            <span className="text-slate-400"> · {grid.data.summary.notLogged} {tr(L.toLog)}</span>
          ) : null}
        </div>
      )}

      <Card>
        <QueryState
          isLoading={grid.isLoading}
          isError={grid.isError}
          error={grid.error}
          isEmpty={isEmpty}
          emptyMessage={tr(L.empty)}
          errorFallback={t('common.error')}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-medium sticky left-0 bg-white z-10 min-w-32 align-bottom">
                    {tr(L.medication)}
                  </th>
                  {days.map((d) => {
                    const { name, dm } = dayHeader(d, locale);
                    const isToday = d === currentWeekStart || d === today();
                    return (
                      <th
                        key={d}
                        className={`py-2 px-2 font-medium text-center min-w-28 align-bottom ${
                          d === today() ? 'text-brand' : ''
                        }`}
                      >
                        <div className="capitalize">{name}</div>
                        <div className={`text-xs ${isToday ? '' : 'text-slate-400'}`}>{dm}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {meds.map((med) => {
                  const label = `${t(`concept.${med.concept}`)}${med.brandName ? ` (${med.brandName})` : ''}`;
                  return (
                    <tr key={med.orderId} className="border-b border-slate-100 align-top">
                      <th
                        scope="row"
                        className="py-2 pr-3 font-medium text-slate-700 text-left sticky left-0 bg-white z-10 align-top"
                      >
                        {label}
                      </th>
                      {days.map((d) => {
                        const slots = cellsByMedDay.get(`${med.orderId} ${d}`) ?? [];
                        return (
                          <td key={d} className="py-2 px-2 align-top">
                            {slots.length === 0 ? (
                              <span className="text-slate-200">&nbsp;</span>
                            ) : (
                              <ul className="space-y-1">
                                {slots.map((s, i) => (
                                  <li key={`${s.time}-${i}`} className="leading-tight">
                                    <SlotCell slot={s} lang={lang} t={t} />
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* Special weight row */}
                <tr className="align-top bg-slate-50/60">
                  <th
                    scope="row"
                    className="py-2 pr-3 font-medium text-slate-700 text-left sticky left-0 bg-slate-50 z-10 align-top"
                  >
                    {t('growth.weight')}
                  </th>
                  {days.map((d) => {
                    const w = weightByDay.get(d);
                    const value = w ? kg(w.weightGrams) : null;
                    if (!value) {
                      return (
                        <td key={d} className="py-2 px-2 text-center text-slate-300">
                          —
                        </td>
                      );
                    }
                    const verified = w?.verified ?? false;
                    return (
                      <td key={d} className="py-2 px-2 text-center">
                        <span className={verified ? 'text-slate-700' : 'text-slate-400'}>{value}</span>
                        {!verified && (
                          <span className="text-amber-500" title={tr(L.unverified)}>
                            {' '}
                            ?
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="font-medium text-slate-600">{tr(L.legend)}:</span>
            <LegendItem icon="✓" className="text-green-700" label={t('admin.taken')} />
            <LegendItem icon="✓" className="text-green-700" label={`${t('admin.late')}`} />
            <LegendItem icon="✗" className="text-red-700" label={`${t('admin.missed')} / ${t('admin.refused')}`} />
            <LegendItem
              icon="◐"
              className="text-amber-700"
              label={`${t('admin.partial')} / ${t('admin.vomited_after_dose')}`}
            />
            <LegendItem icon="—" className="text-slate-400" label={t('admin.unknown')} />
          </div>
        </QueryState>
      </Card>
    </div>
  );
}

function LegendItem({ icon, className, label }: { icon: string; className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className} aria-hidden>
        {icon}
      </span>
      <span>{label}</span>
    </span>
  );
}

/** One scheduled slot: time · status icon · dose (or actual time when shifted). */
function SlotCell({
  slot,
  lang,
  t,
}: {
  slot: GridCell;
  lang: Lang;
  t: (key: string) => string;
}) {
  const tone = STATUS_TONE[slot.status];
  const icon = STATUS_ICON[slot.status];
  const colorClass = TONE_CLASS[tone];

  // Dose label: only when a dose is known and the slot represents a real dose.
  const showDose =
    slot.dose != null &&
    slot.dose !== '' &&
    slot.status !== 'upcoming' &&
    slot.status !== 'held_by_doctor' &&
    slot.status !== 'not_applicable';
  const doseLabel = showDose ? `${slot.dose}${slot.unit ? ` ${slot.unit}` : ''}` : null;

  // Show the actual given time when it differs from the scheduled slot time.
  const shiftedTime =
    slot.actualTime && slot.actualTime !== slot.time ? slot.actualTime : null;

  // Trailing hint for statuses where the icon alone is ambiguous.
  let hint: string | null = null;
  if (slot.status === 'late') hint = L.late[lang];
  else if (slot.status === 'missed' || slot.status === 'refused') hint = t(`admin.${slot.status}`);
  else if (slot.status === 'partial' || slot.status === 'vomited_after_dose') hint = t(`admin.${slot.status}`);
  else if (slot.status === 'held_by_doctor') hint = t('admin.held_by_doctor');

  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1 text-xs ${colorClass}`}>
      <span className="tabular-nums text-slate-600">{slot.time}</span>
      <span aria-hidden>{icon}</span>
      {doseLabel && <span className="tabular-nums">{doseLabel}</span>}
      {shiftedTime && <span className="tabular-nums opacity-80">{shiftedTime}</span>}
      {hint && <span className="opacity-80">{hint}</span>}
    </span>
  );
}
